import React from 'react';
import { Box, Container, Typography, Paper, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import SEO from '../components/common/SEO';
import { pageSEO, generateStructuredData } from '../utils/seo';

const PrivacyPolicyPage: React.FC = () => {
  const theme = useTheme();
  const seoData = pageSEO['/privacy'];
  const structuredData = generateStructuredData('/privacy');
  
  return (
    <>
      <SEO 
        pathname="/privacy" 
        {...seoData} 
        structuredData={structuredData}
      />
      <Box
        sx={{
        minHeight: '100vh',
        py: 8,
        background: `
          linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.05)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.05)} 100%
          ),
          radial-gradient(
            circle at 50% 50%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 0% 100%,
            ${alpha(theme.palette.secondary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 100% 0%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          )
        `,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              circle at 50% 50%,
              ${alpha(theme.palette.primary.main, 0.03)} 0%,
              transparent 70%
            )
          `,
          animation: 'pulse 8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': {
              transform: 'scale(1)',
              opacity: 1,
            },
            '50%': {
              transform: 'scale(1.2)',
              opacity: 0.8,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(
              45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
            ),
            linear-gradient(
              -45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
          )`,
          backgroundSize: '60px 60px',
          opacity: 0.5,
          animation: 'move 20s linear infinite',
          '@keyframes move': {
            '0%': {
              backgroundPosition: '0 0',
            },
            '100%': {
              backgroundPosition: '60px 60px',
            },
          },
          zIndex: 0,
        }}
      />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 6 },
              borderRadius: 3,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography variant="h3" component="h1" gutterBottom>
              Privacy Policy
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" paragraph>
              Last updated: {new Date().toLocaleDateString()}
            </Typography>
            
            <Divider sx={{ my: 4 }} />

            <Box sx={{ '& > section': { mb: 4 } }}>
              <section>
                <Typography variant="h5" gutterBottom>
                  1. Introduction
            </Typography>
            <Typography paragraph>
                  The United Travel ("we," "our," or "us") is committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services.
            </Typography>
              </section>

              <section>
                <Typography variant="h5" gutterBottom>
                  2. Information We Collect
            </Typography>
            <Typography paragraph>
                  We collect several types of information from and about users of our services, including:
            </Typography>
                <ul>
                  <li>Personal identification information (name, email, phone number)</li>
                  <li>Payment information</li>
                  <li>Travel preferences and history</li>
                  <li>Device and usage information</li>
                  <li>Location data</li>
                </ul>
              </section>

              <section>
                <Typography variant="h5" gutterBottom>
                  3. How We Use Your Information
            </Typography>
            <Typography paragraph>
                  We use the information we collect to:
            </Typography>
                <ul>
                  <li>Provide and maintain our services</li>
                  <li>Process your bookings and payments</li>
                  <li>Communicate with you about your bookings</li>
                  <li>Improve our services and customer experience</li>
                  <li>Send you marketing communications (with your consent)</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <Typography variant="h5" gutterBottom>
                  4. Information Sharing
            </Typography>
            <Typography paragraph>
              We may share your information with:
            </Typography>
                <ul>
                  <li>Service providers who assist in our operations</li>
                  <li>Payment processors</li>
                  <li>Law enforcement when required by law</li>
                  <li>Other parties with your consent</li>
                </ul>
              </section>

              <section>
                <Typography variant="h5" gutterBottom>
                  5. Data Security
            </Typography>
                <Typography paragraph>
                  We implement appropriate security measures to protect your personal information. 
                  However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </Typography>
              </section>
            
              <section>
                <Typography variant="h5" gutterBottom>
                  6. Your Rights
                </Typography>
            <Typography paragraph>
                  You have the right to:
            </Typography>
                <ul>
                  <li>Access your personal information</li>
                  <li>Correct inaccurate information</li>
                  <li>Request deletion of your information</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Withdraw consent for data processing</li>
                </ul>
              </section>

              <section>
                <Typography variant="h5" gutterBottom>
                  7. Cookies and Tracking
                </Typography>
            <Typography paragraph>
                  We use cookies and similar tracking technologies to improve your experience on our website. 
                  You can control cookies through your browser settings.
            </Typography>
              </section>

              <section>
                <Typography variant="h5" gutterBottom>
                  8. Children's Privacy
            </Typography>
            <Typography paragraph>
                  Our services are not intended for individuals under 18 years of age. 
                  We do not knowingly collect personal information from children.
            </Typography>
              </section>

              <section>
                <Typography variant="h5" gutterBottom>
                  9. Changes to This Policy
            </Typography>
            <Typography paragraph>
                  We may update this Privacy Policy from time to time. 
                  We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
            </Typography>
              </section>
            
              <section>
                <Typography variant="h5" gutterBottom>
                  10. Contact Us
            </Typography>
                <Typography paragraph>
                  If you have questions about this Privacy Policy, please contact us at:
            </Typography>
                <Typography>
                  Email: contact.theunitedtravels@gmail.com<br />
                  Phone: +91 6351003667<br />
                  Address: F-8 Plot No. 347/2, Third Floor, Surbhi Complex, Sector - 22, Gandhinagar - 382021
            </Typography>
              </section>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
    </>
  );
};

export default PrivacyPolicyPage; 